import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { useTheme } from '../../contexts/ThemeContext';
import {
  Globe, Film, Palette, Smartphone, ShoppingBag, Megaphone, Plus, Pencil, Copy,
  Trash2, GripVertical, ChevronDown, ChevronUp, X, Save, ArrowLeft, Eye, EyeOff,
  Type, AlignLeft, List, CheckSquare, Hash, Calendar, Link as LinkIcon, Upload,
  Paintbrush, Star, Layout, Package, Mic, Settings2, Loader2, Euro
} from 'lucide-react';
import { cn } from '../../lib/utils';

// Field type configurations
const FIELD_TYPES = [
  { value: 'text', label: 'Texte court', icon: Type, description: 'Champ de saisie simple' },
  { value: 'textarea', label: 'Texte long', icon: AlignLeft, description: 'Zone de texte multi-lignes' },
  { value: 'select', label: 'Sélection unique', icon: List, description: 'Liste déroulante' },
  { value: 'multiselect', label: 'Sélection multiple', icon: CheckSquare, description: 'Cases à cocher' },
  { value: 'number', label: 'Nombre', icon: Hash, description: 'Valeur numérique' },
  { value: 'date', label: 'Date', icon: Calendar, description: 'Sélecteur de date' },
  { value: 'url', label: 'Liens URL', icon: LinkIcon, description: 'Un ou plusieurs liens' },
  { value: 'file', label: 'Fichiers', icon: Upload, description: 'Upload de fichiers' },
  { value: 'color', label: 'Couleurs', icon: Paintbrush, description: 'Sélecteur de couleurs hexa' },
  { value: 'scale', label: 'Échelle', icon: Star, description: 'Notation 1-5' },
  { value: 'sitemap', label: 'Arborescence', icon: Layout, description: 'Builder de pages' },
  { value: 'deliverables', label: 'Livrables agence', icon: Package, description: 'Ce que l\'agence va livrer (crée des tâches)' },
  { value: 'client_elements', label: 'Éléments client', icon: Upload, description: 'Ce que le client doit fournir' },
  { value: 'budget_estimate', label: 'Estimation Budget', icon: Euro, description: 'Heures + Taux (Agence)' }
];

// Icon mapping
const ICON_MAP = {
  'Globe': Globe,
  'Film': Film,
  'Palette': Palette,
  'Smartphone': Smartphone,
  'ShoppingBag': ShoppingBag,
  'Megaphone': Megaphone
};

const BriefTemplatesPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state for editing template
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    icon: 'Globe',
    fields: [],
    deliverables: [],
    is_active: true
  });
  
  // Field form state
  const [fieldForm, setFieldForm] = useState({
    id: '',
    field_type: 'text',
    label: '',
    description: '',
    required: false,
    placeholder: '',
    options: [],
    section: 'general',
    order: 0,
    with_voice_input: false,
    agency_only: false
  });

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/brief-templates/?active_only=false');
      setTemplates(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des templates');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setTemplateForm({
      name: template.name,
      description: template.description || '',
      icon: template.icon || 'Globe',
      fields: template.fields || [],
      deliverables: template.deliverables || [],
      is_active: template.is_active !== false
    });
    setIsEditing(false);
  };

  const handleSaveTemplate = async () => {
    if (!selectedTemplate) return;
    
    setSaving(true);
    try {
      await api.put(`/brief-templates/${selectedTemplate.id}`, {
        name: templateForm.name,
        description: templateForm.description,
        icon: templateForm.icon,
        fields: templateForm.fields,
        deliverables: templateForm.deliverables,
        is_active: templateForm.is_active
      });
      
      toast.success('Template sauvegardé avec succès');
      fetchTemplates();
      setIsEditing(false);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicateTemplate = async (template) => {
    try {
      const newName = prompt('Nom du nouveau template:', `${template.name} (copie)`);
      if (!newName) return;
      
      const newType = prompt('Identifiant unique (ex: video_corporate):', `${template.project_type}_copy`);
      if (!newType) return;
      
      await api.post(`/brief-templates/${template.id}/duplicate`, null, {
        params: { new_name: newName, new_project_type: newType }
      });
      
      toast.success('Template dupliqué avec succès');
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la duplication');
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (!confirm(`Désactiver le template "${template.name}" ?`)) return;
    
    try {
      await api.delete(`/brief-templates/${template.id}`);
      toast.success('Template désactivé');
      fetchTemplates();
      if (selectedTemplate?.id === template.id) {
        setSelectedTemplate(null);
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Field management
  const openFieldModal = (field = null) => {
    if (field) {
      setEditingField(field);
      setFieldForm({
        id: field.id,
        field_type: field.field_type,
        label: field.label,
        description: field.description || '',
        required: field.required || false,
        placeholder: field.placeholder || '',
        options: field.options || [],
        section: field.section || 'general',
        order: field.order || 0,
        with_voice_input: field.with_voice_input || false,
        agency_only: field.agency_only || false
      });
    } else {
      setEditingField(null);
      setFieldForm({
        id: `field_${Date.now()}`,
        field_type: 'text',
        label: '',
        description: '',
        required: false,
        placeholder: '',
        options: [],
        section: 'general',
        order: templateForm.fields.length,
        with_voice_input: false,
        agency_only: false
      });
    }
    setShowFieldModal(true);
  };

  const saveField = () => {
    if (!fieldForm.label.trim()) {
      toast.error('Le libellé est requis');
      return;
    }
    
    const newField = { ...fieldForm };
    
    if (editingField) {
      // Update existing field
      setTemplateForm(prev => ({
        ...prev,
        fields: prev.fields.map(f => f.id === editingField.id ? newField : f)
      }));
    } else {
      // Add new field
      setTemplateForm(prev => ({
        ...prev,
        fields: [...prev.fields, newField]
      }));
    }
    
    setShowFieldModal(false);
  };

  const removeField = (fieldId) => {
    setTemplateForm(prev => ({
      ...prev,
      fields: prev.fields.filter(f => f.id !== fieldId)
    }));
  };

  const moveField = (index, direction) => {
    const newFields = [...templateForm.fields];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newFields.length) return;
    
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    newFields.forEach((f, i) => f.order = i);
    
    setTemplateForm(prev => ({ ...prev, fields: newFields }));
  };

  // Options management for select/multiselect fields
  const addOption = () => {
    setFieldForm(prev => ({
      ...prev,
      options: [...prev.options, { value: '', label: '' }]
    }));
  };

  const updateOption = (index, key, value) => {
    setFieldForm(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => 
        i === index ? { ...opt, [key]: value, value: key === 'label' ? value.toLowerCase().replace(/\s+/g, '_') : opt.value } : opt
      )
    }));
  };

  const removeOption = (index) => {
    setFieldForm(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  // Deliverables management
  const addDeliverable = () => {
    const label = prompt('Nom du livrable:');
    if (!label) return;
    
    setTemplateForm(prev => ({
      ...prev,
      deliverables: [...prev.deliverables, {
        id: `deliv_${Date.now()}`,
        label,
        default: false
      }]
    }));
  };

  const toggleDeliverableDefault = (index) => {
    setTemplateForm(prev => ({
      ...prev,
      deliverables: prev.deliverables.map((d, i) => 
        i === index ? { ...d, default: !d.default } : d
      )
    }));
  };

  const removeDeliverable = (index) => {
    setTemplateForm(prev => ({
      ...prev,
      deliverables: prev.deliverables.filter((_, i) => i !== index)
    }));
  };

  const getIconComponent = (iconName) => {
    return ICON_MAP[iconName] || Globe;
  };

  const getFieldIcon = (fieldType) => {
    const config = FIELD_TYPES.find(t => t.value === fieldType);
    return config?.icon || Type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc]">
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        <main className={cn(
          "transition-all duration-300 px-4 md:px-6 pb-6",
          collapsed ? "md:ml-20" : "md:ml-64"
        )} style={{ paddingTop: "130px" }}>
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-4 md:px-6 pb-6",
        collapsed ? "md:ml-20" : "md:ml-64"
      )} style={{ paddingTop: "130px" }}>
        <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Templates de Brief</h1>
            <p className="text-slate-500">Personnalisez les formulaires de brief pour chaque type de projet</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/agency/settings')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux paramètres
          </Button>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Templates List */}
          <div className="col-span-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Types de projets</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {templates.map((template) => {
                  const IconComp = getIconComponent(template.icon);
                  return (
                    <div
                      key={template.id}
                      className={cn(
                        "flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors",
                        selectedTemplate?.id === template.id
                          ? "bg-blue-50 border border-blue-200"
                          : "bg-white hover:bg-slate-50 border border-slate-100"
                      )}
                      onClick={() => handleSelectTemplate(template)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-10 h-10 rounded-lg flex items-center justify-center",
                          template.is_active ? "bg-blue-100 text-blue-600" : "bg-slate-100 text-slate-400"
                        )}>
                          <IconComp className="w-5 h-5" />
                        </div>
                        <div>
                          <p className={cn(
                            "font-medium",
                            !template.is_active && "text-slate-400"
                          )}>
                            {template.name}
                          </p>
                          <p className="text-xs text-slate-500">
                            {template.fields?.length || 0} champs
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={(e) => { e.stopPropagation(); handleDuplicateTemplate(template); }}
                          title="Dupliquer"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        {!template.is_active && (
                          <Badge variant="outline" className="text-xs">Inactif</Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Template Editor */}
          <div className="col-span-8">
            {selectedTemplate ? (
              <Card>
                <CardHeader className="border-b">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>{templateForm.name}</CardTitle>
                      <CardDescription>{templateForm.description}</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      {isEditing ? (
                        <>
                          <Button variant="outline" onClick={() => setIsEditing(false)}>
                            Annuler
                          </Button>
                          <Button onClick={handleSaveTemplate} disabled={saving}>
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            <Save className="w-4 h-4 mr-2" />
                            Sauvegarder
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteTemplate(selectedTemplate)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Désactiver
                          </Button>
                          <Button onClick={() => setIsEditing(true)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Modifier
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="p-6">
                  {isEditing ? (
                    <div className="space-y-6">
                      {/* Basic Info */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Nom du template</Label>
                          <Input
                            value={templateForm.name}
                            onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Icône</Label>
                          <Select
                            value={templateForm.icon}
                            onValueChange={(v) => setTemplateForm(prev => ({ ...prev, icon: v }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(ICON_MAP).map(([name, Icon]) => (
                                <SelectItem key={name} value={name}>
                                  <span className="flex items-center gap-2">
                                    <Icon className="w-4 h-4" />
                                    {name}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Textarea
                          value={templateForm.description}
                          onChange={(e) => setTemplateForm(prev => ({ ...prev, description: e.target.value }))}
                          rows={2}
                        />
                      </div>

                      <div className="flex items-center gap-2">
                        <Switch
                          checked={templateForm.is_active}
                          onCheckedChange={(v) => setTemplateForm(prev => ({ ...prev, is_active: v }))}
                        />
                        <Label>Template actif</Label>
                      </div>

                      {/* Fields */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="text-base font-semibold">Champs du formulaire</Label>
                          <Button variant="outline" size="sm" onClick={() => openFieldModal()}>
                            <Plus className="w-4 h-4 mr-1" />
                            Ajouter un champ
                          </Button>
                        </div>
                        
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                          {templateForm.fields
                            .sort((a, b) => (a.order || 0) - (b.order || 0))
                            .map((field, index) => {
                              const FieldIcon = getFieldIcon(field.field_type);
                              return (
                                <div
                                  key={field.id}
                                  className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg group"
                                >
                                  <GripVertical className="w-4 h-4 text-slate-400 cursor-grab" />
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <FieldIcon className="w-4 h-4 text-slate-500" />
                                      <span className="font-medium text-sm">{field.label}</span>
                                      {field.required && (
                                        <Badge variant="secondary" className="text-xs">Requis</Badge>
                                      )}
                                      {field.with_voice_input && (
                                        <Mic className="w-3 h-3 text-blue-500" />
                                      )}
                                      {field.agency_only && (
                                        <EyeOff className="w-3 h-3 text-orange-500" title="Agence uniquement" />
                                      )}
                                    </div>
                                    <p className="text-xs text-slate-500">
                                      {FIELD_TYPES.find(t => t.value === field.field_type)?.label}
                                      {field.section && ` • Section: ${field.section}`}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0"
                                      onClick={() => moveField(index, -1)}
                                      disabled={index === 0}
                                    >
                                      <ChevronUp className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0"
                                      onClick={() => moveField(index, 1)}
                                      disabled={index === templateForm.fields.length - 1}
                                    >
                                      <ChevronDown className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0"
                                      onClick={() => openFieldModal(field)}
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                      onClick={() => removeField(field.id)}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>

                      {/* Deliverables */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="text-base font-semibold">Livrables par défaut</Label>
                          <Button variant="outline" size="sm" onClick={addDeliverable}>
                            <Plus className="w-4 h-4 mr-1" />
                            Ajouter
                          </Button>
                        </div>
                        
                        <div className="space-y-2">
                          {templateForm.deliverables.map((deliv, index) => (
                            <div
                              key={deliv.id}
                              className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg"
                            >
                              <Package className="w-4 h-4 text-slate-400" />
                              <span className="flex-1 text-sm">{deliv.label}</span>
                              <div className="flex items-center gap-2">
                                <label className="flex items-center gap-1 text-xs text-slate-500">
                                  <input
                                    type="checkbox"
                                    checked={deliv.default}
                                    onChange={() => toggleDeliverableDefault(index)}
                                    className="rounded"
                                  />
                                  Coché par défaut
                                </label>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 text-red-500"
                                  onClick={() => removeDeliverable(index)}
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* View Mode */
                    <div className="space-y-6">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-slate-500">Type de projet</p>
                          <p className="font-medium">{selectedTemplate.project_type}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Statut</p>
                          <Badge variant={selectedTemplate.is_active ? "default" : "secondary"}>
                            {selectedTemplate.is_active ? 'Actif' : 'Inactif'}
                          </Badge>
                        </div>
                      </div>

                      <div>
                        <p className="text-sm text-slate-500 mb-2">Champs du formulaire ({templateForm.fields.length})</p>
                        <div className="space-y-1">
                          {templateForm.fields
                            .sort((a, b) => (a.order || 0) - (b.order || 0))
                            .map((field) => {
                              const FieldIcon = getFieldIcon(field.field_type);
                              return (
                                <div key={field.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded text-sm">
                                  <FieldIcon className="w-4 h-4 text-slate-400" />
                                  <span>{field.label}</span>
                                  {field.required && <Badge variant="secondary" className="text-xs">Requis</Badge>}
                                  {field.with_voice_input && <Mic className="w-3 h-3 text-blue-500" />}
                                </div>
                              );
                            })}
                        </div>
                      </div>

                      <div>
                        <p className="text-sm text-slate-500 mb-2">Livrables ({templateForm.deliverables.length})</p>
                        <div className="flex flex-wrap gap-2">
                          {templateForm.deliverables.map((deliv) => (
                            <Badge key={deliv.id} variant={deliv.default ? "default" : "outline"}>
                              {deliv.label}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="h-full flex items-center justify-center min-h-[400px]">
                <div className="text-center text-slate-500">
                  <Settings2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Sélectionnez un template pour le modifier</p>
                </div>
              </Card>
            )}
          </div>
        </div>
        </div>

      {/* Field Edit Modal */}
      <Dialog open={showFieldModal} onOpenChange={setShowFieldModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingField ? 'Modifier le champ' : 'Ajouter un champ'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Type de champ</Label>
              <Select
                value={fieldForm.field_type}
                onValueChange={(v) => setFieldForm(prev => ({ ...prev, field_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((type) => {
                    const Icon = type.icon;
                    return (
                      <SelectItem key={type.value} value={type.value}>
                        <span className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          {type.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Libellé *</Label>
              <Input
                value={fieldForm.label}
                onChange={(e) => setFieldForm(prev => ({ ...prev, label: e.target.value }))}
                placeholder="Ex: Description du projet"
              />
            </div>

            <div className="space-y-2">
              <Label>Description / Aide</Label>
              <Input
                value={fieldForm.description}
                onChange={(e) => setFieldForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Texte d'aide affiché sous le champ"
              />
            </div>

            <div className="space-y-2">
              <Label>Placeholder</Label>
              <Input
                value={fieldForm.placeholder}
                onChange={(e) => setFieldForm(prev => ({ ...prev, placeholder: e.target.value }))}
                placeholder="Texte affiché dans le champ vide"
              />
            </div>

            <div className="space-y-2">
              <Label>Section</Label>
              <Select
                value={fieldForm.section}
                onValueChange={(v) => setFieldForm(prev => ({ ...prev, section: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">Général</SelectItem>
                  <SelectItem value="specifications">Spécifications</SelectItem>
                  <SelectItem value="technical">Technique</SelectItem>
                  <SelectItem value="structure">Structure</SelectItem>
                  <SelectItem value="inspiration">Inspiration</SelectItem>
                  <SelectItem value="colors">Couleurs</SelectItem>
                  <SelectItem value="style">Style</SelectItem>
                  <SelectItem value="features">Fonctionnalités</SelectItem>
                  <SelectItem value="audio">Audio</SelectItem>
                  <SelectItem value="shooting">Tournage</SelectItem>
                  <SelectItem value="post">Post-production</SelectItem>
                  <SelectItem value="deliverables">Livrables</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Options for select/multiselect */}
            {(fieldForm.field_type === 'select' || fieldForm.field_type === 'multiselect') && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Options</Label>
                  <Button variant="outline" size="sm" onClick={addOption}>
                    <Plus className="w-3 h-3 mr-1" />
                    Ajouter
                  </Button>
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {fieldForm.options.map((opt, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        value={opt.label}
                        onChange={(e) => updateOption(index, 'label', e.target.value)}
                        placeholder="Label de l'option"
                        className="flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-red-500"
                        onClick={() => removeOption(index)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={fieldForm.required}
                  onChange={(e) => setFieldForm(prev => ({ ...prev, required: e.target.checked }))}
                  className="rounded"
                />
                <span className="text-sm">Champ obligatoire</span>
              </label>
              
              {(fieldForm.field_type === 'textarea' || fieldForm.field_type === 'text') && (
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={fieldForm.with_voice_input}
                    onChange={(e) => setFieldForm(prev => ({ ...prev, with_voice_input: e.target.checked }))}
                    className="rounded"
                  />
                  <span className="text-sm flex items-center gap-1">
                    <Mic className="w-3 h-3" /> Dictée vocale
                  </span>
                </label>
              )}
              
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={fieldForm.agency_only}
                  onChange={(e) => setFieldForm(prev => ({ ...prev, agency_only: e.target.checked }))}
                  className="rounded"
                />
                <span className="text-sm flex items-center gap-1">
                  <EyeOff className="w-3 h-3" /> Agence uniquement
                </span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFieldModal(false)}>
              Annuler
            </Button>
            <Button onClick={saveField}>
              {editingField ? 'Mettre à jour' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </main>
    </div>
  );
};

export default BriefTemplatesPage;
